"use client";

import Link from "next/link";
import Image from "next/image";
import { Blog } from "../../../../lib/blogApi";

interface ClientComponentProps {
  blog: Blog;
}

export default function ClientComponent({ blog }: ClientComponentProps) {
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.warn("Error formatting date:", error);
      return dateString;
    }
  };

  const imageBaseUrl = process.env.NEXT_PUBLIC_IMAGE_URL || "https://lit-000.litfusion.litpl.com";

  const getImageUrl = (attachment: string | null | undefined) => {
    if (!attachment) return null;
    try {
      const cleanAttachment = attachment.startsWith("/") ? attachment : `/${attachment}`;
      return `${imageBaseUrl}${cleanAttachment}`;
    } catch (error) {
      console.warn("Error constructing image URL:", error);
      return null;
    }
  };

  if (!blog) {
    return (
      <div className="container mx-auto py-10 px-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Blog Post Not Found</h1>
          <p className="text-gray-600 mb-6">The requested blog post could not be found.</p>
          <Link href="/blog" className="text-blue-600 hover:underline">
            ← Back to All Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="max-w-4xl mx-auto px-4">
      <Link href="/blog" className="text-blue-600 hover:underline mb-6 inline-block">
        ← Back to All Blogs
      </Link>

      <header className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">{blog.Title || "Untitled"}</h1>
        <div className="text-gray-600 mb-6">
          <time dateTime={blog.CreateDate || ""}>
            Published: {formatDate(blog.CreateDate)}
          </time>
          {blog.UpdateDate && blog.UpdateDate !== blog.CreateDate && (
            <span> • Updated: {formatDate(blog.UpdateDate)}</span>
          )}
        </div>

        {(() => {
          const imageUrl = getImageUrl(blog.Attachment);
          return imageUrl ? (
            <div className="w-full h-[400px] relative mb-8 rounded-lg overflow-hidden bg-gray-100">
              <Image
                src={imageUrl}
                alt={blog.Title || "Blog image"}
                fill
                className="object-cover transition-opacity duration-300"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 896px"
                priority
                quality={85}
                loading="eager"
                placeholder="blur"
                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                onError={(e) => {
                  console.warn("Image failed to load:", imageUrl);
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                }}
                onLoad={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.opacity = "1";
                }}
              />
            </div>
          ) : null;
        })()}
      </header>

      {blog.Content ? (
        <div
          className="max-w-none text-base leading-relaxed prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: blog.Content }}
        />
      ) : blog.Excerpt ? (
        <div className="max-w-none text-base leading-relaxed">
          <p className="text-gray-700">{blog.Excerpt}</p>
        </div>
      ) : (
        <div className="max-w-none text-base leading-relaxed">
          <p className="text-gray-500 italic">No content available for this blog post.</p>
        </div>
      )}

      {blog.Keywords && (
        <div className="mt-12 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-semibold mb-2">Keywords</h3>
          <div className="flex flex-wrap gap-2">
            {(() => {
              try {
                return blog.Keywords.split(",").map((keyword, index) => (
                  <span key={index} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                    {keyword.trim()}
                  </span>
                ));
              } catch (error) {
                console.warn("Error parsing keywords:", error);
                return (
                  <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                    {blog.Keywords}
                  </span>
                );
              }
            })()}
          </div>
        </div>
      )}
    </article>
  );
}
